<template>
    <div class="stats">
        <!-- filters -->
        <div class="stats-filters">
            <div class="stats-presets" role="group" aria-label="Περίοδος">
                <button
                    v-for="preset in presets"
                    :key="preset.key"
                    type="button"
                    class="stats-preset"
                    :class="{ 'stats-preset--active': period === preset.key }"
                    @click="choosePreset(preset.key)"
                >
                    {{ preset.label }}
                </button>
            </div>
            <div class="stats-fields">
                <label class="stats-date">
                    <span>Από</span>
                    <input v-model="from" type="date" :max="to || today" @change="period = 'custom'; load()">
                </label>
                <label class="stats-date">
                    <span>Έως</span>
                    <input v-model="to" type="date" :min="from" :max="today" @change="period = 'custom'; load()">
                </label>
                <v-select
                    v-model="partner"
                    :items="options.partners"
                    item-text="title"
                    item-value="id"
                    label="Συνεργάτης"
                    outlined
                    dense
                    hide-details
                    clearable
                    class="stats-select"
                    no-data-text="Καμία συναλλαγή με συνεργάτη"
                    @change="load()"
                />
                <v-select
                    v-if="options.teams.length > 1"
                    v-model="team"
                    :items="options.teams"
                    item-text="title"
                    item-value="id"
                    label="Σωματείο"
                    outlined
                    dense
                    hide-details
                    clearable
                    class="stats-select"
                    @change="load()"
                />
            </div>
        </div>

        <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

        <div v-if="!stats && loading" class="stats-kpis">
            <v-skeleton-loader v-for="i in 4" :key="i" type="image" height="104" class="stats-kpi-skeleton" />
        </div>

        <template v-else-if="stats">
            <div class="stats-kpis" :class="{ 'stats--loading': loading }">
                <div class="stats-kpi">
                    <div class="stats-kpi__label"><v-icon small>mdi-account-group-outline</v-icon>Μέλη</div>
                    <div class="stats-kpi__value">{{ number(stats.summary.members) }}</div>
                    <div class="stats-kpi__note">
                        <template v-if="hasPeriod"><strong>+{{ number(stats.summary.newMembers) }}</strong> νέα στην περίοδο</template>
                        <template v-else>{{ number(options.teams.length) }} {{ options.teams.length === 1 ? 'σωματείο' : 'σωματεία' }}</template>
                    </div>
                </div>
                <div class="stats-kpi">
                    <div class="stats-kpi__label"><v-icon small>mdi-receipt-text-outline</v-icon>Συναλλαγές</div>
                    <div class="stats-kpi__value">{{ number(stats.summary.transactions) }}</div>
                    <div class="stats-kpi__note">
                        <span v-if="change('transactions') !== null" :class="trendClass('transactions')">{{ trendText('transactions') }}</span>
                        {{ number(stats.summary.completed) }} επιβεβαιωμένες
                    </div>
                </div>
                <div class="stats-kpi stats-kpi--accent">
                    <div class="stats-kpi__label"><v-icon small>mdi-cash-check</v-icon>Επιβεβαιωμένο ποσό</div>
                    <div class="stats-kpi__value">{{ money(stats.summary.amount) }}</div>
                    <div class="stats-kpi__note">
                        <span v-if="change('amount') !== null" :class="trendClass('amount')">{{ trendText('amount') }}</span>
                        μέση απόδειξη {{ money(stats.summary.average) }}
                    </div>
                </div>
                <div class="stats-kpi stats-kpi--pending">
                    <div class="stats-kpi__label"><v-icon small>mdi-timer-sand</v-icon>Σε εκκρεμότητα ΑΑΔΕ</div>
                    <div class="stats-kpi__value">{{ money(stats.summary.pendingAmount) }}</div>
                    <div class="stats-kpi__note">{{ number(stats.summary.pending) }} αποδείξεις</div>
                </div>
            </div>

            <section class="stats-card" :class="{ 'stats--loading': loading }">
                <div class="stats-card__head">
                    <h3>Ανά μήνα</h3>
                    <div class="stats-toggle" role="group" aria-label="Μέγεθος">
                        <button type="button" :class="{ 'stats-toggle--active': metric === 'transactions' }" @click="metric = 'transactions'; showLatestMonths()">Συναλλαγές</button>
                        <button type="button" :class="{ 'stats-toggle--active': metric === 'amount' }" @click="metric = 'amount'; showLatestMonths()">Ποσό</button>
                    </div>
                </div>
                <div v-if="monthlyMax" ref="chart" class="stats-chart">
                    <div v-for="month in stats.monthly" :key="month.month" class="stats-chart__col" :title="monthTitle(month)">
                        <span class="stats-chart__value">{{ month[metric] ? (metric === 'amount' ? shortMoney(month.amount) : month.transactions) : '' }}</span>
                        <div class="stats-chart__bar" :style="{ height: barHeight(month[metric]) }" />
                        <span class="stats-chart__label">{{ monthLabel(month.month) }}</span>
                    </div>
                </div>
                <p v-else class="stats-empty">Δεν υπάρχουν {{ metric === 'amount' ? 'επιβεβαιωμένα ποσά' : 'συναλλαγές' }} σε αυτή την περίοδο.</p>
            </section>

            <div class="stats-columns" :class="{ 'stats--loading': loading }">
                <section class="stats-card">
                    <div class="stats-card__head"><h3>Κορυφαίοι συνεργάτες</h3></div>
                    <ul v-if="stats.partners.length" class="stats-ranking">
                        <li v-for="item in stats.partners" :key="item.id">
                            <div class="stats-ranking__row">
                                <span class="stats-ranking__title">{{ item.title }}</span>
                                <span class="stats-ranking__value">{{ item.transactions }} · {{ money(item.amount) }}</span>
                            </div>
                            <div class="stats-ranking__track">
                                <div class="stats-ranking__fill" :style="{ width: share(item.transactions, partnersMax) }" />
                            </div>
                        </li>
                    </ul>
                    <p v-else class="stats-empty">Καμία συναλλαγή με συνεργάτη σε αυτή την περίοδο.</p>
                </section>

                <section class="stats-card">
                    <div class="stats-card__head"><h3>Ανά σωματείο</h3></div>
                    <div v-if="stats.teams.length" class="stats-table-wrap">
                    <table class="stats-table">
                        <thead>
                            <tr><th>Σωματείο</th><th>Μέλη</th><th>Συναλλ.</th><th>Ποσό</th></tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in stats.teams" :key="row.id">
                                <td>{{ row.title }}</td>
                                <td>{{ number(row.members) }}</td>
                                <td>{{ number(row.transactions) }}</td>
                                <td>{{ money(row.amount) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    </div>
                    <p v-else class="stats-empty">Δεν σας έχουν ανατεθεί σωματεία.</p>
                </section>
            </div>

            <p class="stats-footnote">
                Οι συναλλαγές μετρώνται με την ημερομηνία της απόδειξης. Τα ποσά περιλαμβάνουν μόνο αποδείξεις επιβεβαιωμένες από την ΑΑΔΕ.
            </p>
        </template>
    </div>
</template>

<script>
import dealerService from '@/services/dealer'

const toIso = date => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 10)
}

// Dashboard of a dealer's members and transactions, shown in their profile
export default {
    name: 'DealerStats',
    data: () => ({
        presets: [
            { key: 'month', label: 'Τρέχων μήνας' },
            { key: '3m', label: '3 μήνες' },
            { key: '12m', label: '12 μήνες' },
            { key: 'year', label: 'Φέτος' },
            { key: 'all', label: 'Όλα' }
        ],
        period: '12m',
        from: '',
        to: '',
        partner: null,
        team: null,
        metric: 'transactions',
        stats: null,
        loading: false,
        error: null,
        today: toIso(new Date()),
        requestId: 0
    }),
    computed: {
        options() {
            return (this.stats && this.stats.options) || { teams: [], partners: [] }
        },
        hasPeriod() {
            return !!(this.from || this.to)
        },
        monthlyMax() {
            return this.stats ? Math.max(0, ...this.stats.monthly.map(month => month[this.metric])) : 0
        },
        partnersMax() {
            return this.stats ? Math.max(0, ...this.stats.partners.map(item => item.transactions)) : 0
        }
    },
    created() {
        this.choosePreset(this.period)
    },
    methods: {
        choosePreset(key) {
            const now = new Date()
            const start = {
                month: new Date(now.getFullYear(), now.getMonth(), 1),
                '3m': new Date(now.getFullYear(), now.getMonth() - 2, 1),
                '12m': new Date(now.getFullYear(), now.getMonth() - 11, 1),
                year: new Date(now.getFullYear(), 0, 1)
            }[key]
            this.period = key
            this.from = start ? toIso(start) : ''
            this.to = start ? this.today : ''
            this.load()
        },
        load() {
            // only the answer to the latest request is shown when filters change quickly
            const id = ++this.requestId
            this.loading = true
            this.error = null
            const filter = {}
            if (this.from) filter.from = this.from
            if (this.to) filter.to = this.to
            if (this.partner) filter.partner = this.partner
            if (this.team) filter.team = this.team
            dealerService.getStats(filter).then(stats => {
                if (id === this.requestId) {
                    this.stats = stats
                    this.showLatestMonths()
                }
            }).catch(() => {
                if (id === this.requestId) {
                    this.error = 'Δεν ήταν δυνατή η φόρτωση των στατιστικών. Δοκιμάστε ξανά σε λίγο.'
                }
            }).finally(() => {
                if (id === this.requestId) {
                    this.loading = false
                }
            })
        },
        // long periods scroll sideways: show the latest month that has figures, not the oldest months
        showLatestMonths() {
            this.$nextTick(() => {
                const chart = this.$refs.chart
                if (!chart || !this.stats) {
                    return
                }
                const months = this.stats.monthly
                let last = months.length - 1
                while (last > 0 && !months[last][this.metric]) {
                    last--
                }
                const column = chart.children[last]
                chart.scrollLeft = column ? column.offsetLeft + column.offsetWidth - chart.clientWidth + 24 : chart.scrollWidth
            })
        },
        change(key) {
            const previous = this.stats.previous
            if (!previous || !previous[key]) {
                return null
            }
            return (this.stats.summary[key] - previous[key]) / previous[key]
        },
        trendText(key) {
            const value = this.change(key)
            return `${value >= 0 ? '▲' : '▼'} ${Math.abs(Math.round(value * 100))}%`
        },
        trendClass(key) {
            return this.change(key) >= 0 ? 'stats-trend stats-trend--up' : 'stats-trend stats-trend--down'
        },
        number(value) {
            return Number(value || 0).toLocaleString('el-GR')
        },
        money(value) {
            return Number(value || 0).toLocaleString('el-GR', { style: 'currency', currency: 'EUR' })
        },
        shortMoney(value) {
            return value >= 1000 ? `${(value / 1000).toLocaleString('el-GR', { maximumFractionDigits: 1 })}k€` : `${Math.round(value)}€`
        },
        monthLabel(key) {
            const [year, month] = key.split('-').map(Number)
            const label = new Date(year, month - 1, 1).toLocaleDateString('el-GR', { month: 'short' })
            return month === 1 || this.stats.monthly[0].month === key ? `${label} ${String(year).slice(2)}` : label
        },
        monthTitle(month) {
            const [year, number] = month.month.split('-').map(Number)
            const name = new Date(year, number - 1, 1).toLocaleDateString('el-GR', { month: 'long', year: 'numeric' })
            return `${name}: ${month.transactions} συναλλαγές, ${this.money(month.amount)}`
        },
        barHeight(value) {
            return this.monthlyMax ? `${Math.max(value ? 4 : 0, Math.round((value / this.monthlyMax) * 100))}%` : '0%'
        },
        share(value, max) {
            return max ? `${Math.max(4, Math.round((value / max) * 100))}%` : '0%'
        }
    }
}
</script>

<style scoped src="@/styles/stats.css"></style>
